/**
 * Intake (WizzyMobile PWA) base URL resolution for cross-app links from the CRM SPA.
 * Mirrors backend `get_intake_base_url()` env mapping.
 */

/** Canonical Service Bureau home route on intake (`/servicebureau/*`). */
export const INTAKE_SERVICE_BUREAU_HOME_PATH = "/servicebureau/home";

/** Canonical tax-pro home route on intake. */
export const INTAKE_PRO_HOME_PATH = "/pro/home";

/** Canonical client home route on intake. */
export const INTAKE_CLIENT_HOME_PATH = "/client/home";

/** Canonical client signup route on intake. */
export const INTAKE_CREATE_ACCOUNT_PATH = "/create-account";

/** Canonical "hire your Wizzy" onboarding route on intake. */
export const INTAKE_GETTING_STARTED_PATH = "/getting-started";

/**
 * Resolve the intake app origin for the current build environment.
 */
export function getIntakeBaseUrl(): string {
  const configured = import.meta.env.VITE_INTAKE_URL;
  if (configured) {
    return configured.replace(/\/$/, "");
  }

  if (import.meta.env.DEV) {
    return "http://localhost:5555";
  }

  if (import.meta.env.VITE_APP_ENV === "staging") {
    return "https://m.smartwiz.link";
  }

  return "https://m.smartwiz.io";
}

/**
 * Build a full intake URL for a path (defaults to Service Bureau home).
 */
export function getIntakeUrl(
  path: string = INTAKE_SERVICE_BUREAU_HOME_PATH
): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${getIntakeBaseUrl()}${normalizedPath}`;
}

/**
 * Full URL for the Service Bureau workspace home on intake.
 */
export function getIntakeServiceBureauHomeUrl(): string {
  return getIntakeUrl(INTAKE_SERVICE_BUREAU_HOME_PATH);
}

/** Full URL for the tax-pro onboarding wizard on intake. */
export function getIntakeGettingStartedUrl(): string {
  return getIntakeUrl(INTAKE_GETTING_STARTED_PATH);
}

/** Full URL for the tax-pro home page on intake. */
export function getIntakeProHomeUrl(): string {
  return getIntakeUrl(INTAKE_PRO_HOME_PATH);
}

/** Full URL for the client home page on intake. */
export function getIntakeClientHomeUrl(): string {
  return getIntakeUrl(INTAKE_CLIENT_HOME_PATH);
}

/** Query params for redirecting legacy register links into intake signup. */
export interface IntakeCreateAccountUrlParams {
  businessId: string;
  businessName: string;
  assigneeId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

/**
 * Build the intake client signup URL used by migrated businesses.
 */
export function buildIntakeCreateAccountUrl(
  params: IntakeCreateAccountUrlParams
): string {
  const search = new URLSearchParams({
    businessId: params.businessId,
    businessName: params.businessName,
  });

  if (params.assigneeId) {
    search.set("assigneeId", params.assigneeId);
  }
  if (params.email) {
    search.set("email", params.email);
  }
  if (params.firstName) {
    search.set("firstName", params.firstName);
  }
  if (params.lastName) {
    search.set("lastName", params.lastName);
  }
  if (params.phoneNumber) {
    search.set("phoneNumber", params.phoneNumber);
  }

  return `${getIntakeBaseUrl()}${INTAKE_CREATE_ACCOUNT_PATH}?${search.toString()}`;
}
